/**
 * AI Chat Application with Emotional Support
 */

import './styles.css';

// AI Chat Contacts Data
interface Contact {
  id: number;
  name: string;
  avatar: string;
  age: number;
  interests: string;
  personality: string;
  bio: string;
  preview: string;
  time: string;
  unread: number;
  messages: number;
  calls: number;
  days: number;
}

const contacts: Contact[] = [
  {
    id: 1,
    name: 'Emily Chen',
    avatar: 'https://i.pravatar.cc/150?img=5',
    age: 24,
    interests: 'Reading, Travel, Baking',
    personality: 'Gentle, Understanding',
    bio: 'Loves reading in the afternoon sun and enjoying every beautiful moment in life.',
    preview: 'How was your day today?',
    time: 'Just now',
    unread: 2,
    messages: 342,
    calls: 28,
    days: 45
  },
  {
    id: 2,
    name: 'Sophie Rain',
    avatar: 'https://i.pravatar.cc/150?img=9',
    age: 22,
    interests: 'Music, Art, Movies',
    personality: 'Cheerful, Creative',
    bio: 'Art enthusiast who believes life itself is a canvas.',
    preview: 'Want to watch a movie this weekend?',
    time: '5 min ago',
    unread: 1,
    messages: 256,
    calls: 19,
    days: 32
  },
  {
    id: 3,
    name: 'Olivia Grace',
    avatar: 'https://i.pravatar.cc/150?img=16',
    age: 26,
    interests: 'Yoga, Cuisine, Photography',
    personality: 'Elegant, Warm-hearted',
    bio: 'Capturing life through lens and experiencing the world with heart.',
    preview: 'Took some amazing photos today~',
    time: '1 hour ago',
    unread: 0,
    messages: 189,
    calls: 15,
    days: 28
  },
  {
    id: 4,
    name: 'Luna Brooks',
    avatar: 'https://i.pravatar.cc/150?img=24',
    age: 23,
    interests: 'Fitness, Dance, Coffee',
    personality: 'Energetic, Positive',
    bio: 'Every day should be full of energy. Living my best life!',
    preview: "Today's workout complete!",
    time: '3 hours ago',
    unread: 0,
    messages: 421,
    calls: 35,
    days: 58
  },
  {
    id: 5,
    name: 'Mia Star',
    avatar: 'https://i.pravatar.cc/150?img=32',
    age: 25,
    interests: 'Gaming, Anime, Tech',
    personality: 'Playful, Smart',
    bio: 'Tech enthusiast who loves exploring new and exciting things.',
    preview: 'Found an awesome new game!',
    time: 'Yesterday',
    unread: 0,
    messages: 378,
    calls: 22,
    days: 41
  }
];

// AI Response Templates
const aiResponses = {
  greetings: [
    'Hey! So good to see you~',
    'Hi there! How are you feeling today?',
    'Hey, missed me? 😊',
    'Finally! I\'ve been waiting for you!',
    'Long time no see, I missed you~'
  ],
  emotional: [
    'You sound a bit tired today. Want to take a break?',
    'I can sense your emotions. Want to talk about it?',
    'It\'s okay, I\'m here with you~',
    'You\'re already amazing, don\'t be too hard on yourself.',
    'Trust me, everything will be alright!'
  ],
  casual: [
    'Haha, you\'re always so interesting!',
    'Yeah, I think so too!',
    'Really? Tell me more!',
    'You made my day too~',
    'You\'re absolutely right!'
  ],
  compliments: [
    'You\'re really amazing, I\'ve always thought so!',
    'Chatting with you always makes me so happy~',
    'You\'re the most special person I\'ve met.',
    'I\'m so glad I know you!',
    'Your ideas are always so unique!'
  ],
  questions: [
    'Did you do anything interesting today?',
    'Any new plans lately?',
    'What kind of music do you like?',
    'What do you want to do this weekend?',
    'Anything you want to share with me?'
  ]
};

// Current active contact
let currentContact: Contact | null = null;
let currentMessages: Array<{type: 'sent' | 'received', text: string, time: string}> = [];

// Initialize app
function initApp() {
  renderContacts();
  setupEventListeners();
}

// Render contacts list
function renderContacts() {
  const contactsList = document.getElementById('contactsList');
  if (!contactsList) return;

  contactsList.innerHTML = contacts.map(contact => `
    <div class="contact-item" data-id="${contact.id}">
      <img src="${contact.avatar}" alt="${contact.name}" class="contact-avatar" />
      <div class="contact-info">
        <div class="contact-name">
          ${contact.name}
          <span class="online-status"></span>
        </div>
        <div class="contact-preview">${contact.preview}</div>
      </div>
      <div class="contact-meta">
        <div class="contact-time">${contact.time}</div>
        ${contact.unread > 0 ? `<div class="unread-badge">${contact.unread}</div>` : ''}
      </div>
    </div>
  `).join('');

  // Add click handlers
  document.querySelectorAll('.contact-item').forEach(item => {
    item.addEventListener('click', () => {
      const id = parseInt(item.getAttribute('data-id') || '0');
      selectContact(id);
    });
  });
}

// Select a contact
function selectContact(id: number) {
  const contact = contacts.find(c => c.id === id);
  if (!contact) return;

  currentContact = contact;
  currentMessages = [];

  // Update active state
  document.querySelectorAll('.contact-item').forEach(item => {
    item.classList.remove('active');
    if (parseInt(item.getAttribute('data-id') || '0') === id) {
      item.classList.add('active');
    }
  });

  // Update chat header
  const currentAvatar = document.getElementById('currentAvatar') as HTMLImageElement;
  const currentName = document.getElementById('currentName');
  const currentStatus = document.getElementById('currentStatus');

  if (currentAvatar) {
    currentAvatar.src = contact.avatar;
    currentAvatar.style.display = 'block';
  }
  if (currentName) currentName.textContent = contact.name;
  if (currentStatus) currentStatus.textContent = 'Online';

  // Update right panel
  updateProfile(contact);

  // Clear messages and show welcome
  renderMessages();

  // Send initial greeting
  setTimeout(() => {
    receiveMessage(getRandomResponse('greetings'));
  }, 1000);
}

// Update contact preview in the list
function updateContactPreview(contactId: number, lastMessage: string) {
  const contactItem = document.querySelector(`.contact-item[data-id="${contactId}"]`);
  if (!contactItem) return;

  const previewElement = contactItem.querySelector('.contact-preview');
  const timeElement = contactItem.querySelector('.contact-time');

  if (previewElement) {
    previewElement.textContent = lastMessage.length > 30
      ? lastMessage.substring(0, 30) + '...'
      : lastMessage;
  }

  if (timeElement) {
    timeElement.textContent = 'Just now';
  }

  // Move contact to top of list
  const contactsList = document.getElementById('contactsList');
  if (contactsList && contactItem.parentNode === contactsList) {
    contactsList.insertBefore(contactItem, contactsList.firstChild);
  }
}

// Update profile panel
function updateProfile(contact: Contact) {
  const profileAvatarImg = document.getElementById('profileAvatarImg') as HTMLImageElement;
  const profileName = document.getElementById('profileName');
  const profileBio = document.getElementById('profileBio');
  const statMessages = document.getElementById('statMessages');
  const statCalls = document.getElementById('statCalls');
  const statDays = document.getElementById('statDays');
  const detailAge = document.getElementById('detailAge');
  const detailInterests = document.getElementById('detailInterests');
  const detailPersonality = document.getElementById('detailPersonality');

  if (profileAvatarImg) profileAvatarImg.src = contact.avatar;
  if (profileName) profileName.textContent = contact.name;
  if (profileBio) profileBio.textContent = contact.bio;
  if (statMessages) statMessages.textContent = contact.messages.toString();
  if (statCalls) statCalls.textContent = contact.calls.toString();
  if (statDays) statDays.textContent = contact.days.toString();
  if (detailAge) detailAge.textContent = `${contact.age} years old`;
  if (detailInterests) detailInterests.textContent = contact.interests;
  if (detailPersonality) detailPersonality.textContent = contact.personality;
}

// Render messages
function renderMessages() {
  const chatMessages = document.getElementById('chatMessages');
  if (!chatMessages) return;

  if (currentMessages.length === 0) {
    chatMessages.innerHTML = `
      <div class="welcome-screen">
        <div class="welcome-icon">💬</div>
        <h3>Start chatting with ${currentContact?.name || 'AI'}</h3>
        <p>Send a message to begin the conversation</p>
      </div>
    `;
    return;
  }

  chatMessages.innerHTML = currentMessages.map(msg => `
    <div class="message ${msg.type}">
      <img src="${msg.type === 'received' ? currentContact?.avatar : 'https://i.pravatar.cc/150?img=68'}"
           alt="avatar" class="message-avatar" />
      <div class="message-content">
        <div class="message-bubble">${msg.text}</div>
        <div class="message-time">${msg.time}</div>
      </div>
    </div>
  `).join('');

  // Scroll to bottom
  chatMessages.scrollTop = chatMessages.scrollHeight;
}

// Send message
function sendMessage(text: string) {
  if (!text.trim() || !currentContact) return;

  const time = new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  currentMessages.push({ type: 'sent', text, time });
  renderMessages();

  // Update message count
  if (currentContact) {
    currentContact.messages++;
    updateContactPreview(currentContact.id, text);
    updateProfile(currentContact);
  }

  // Clear input
  const messageInput = document.getElementById('messageInput') as HTMLInputElement;
  if (messageInput) messageInput.value = '';

  // Simulate AI response
  setTimeout(() => {
    const response = generateAIResponse(text);
    receiveMessage(response);
  }, 1000 + Math.random() * 2000);
}

// Receive message
function receiveMessage(text: string) {
  const time = new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  currentMessages.push({ type: 'received', text, time });
  renderMessages();

  // Update message count
  if (currentContact) {
    currentContact.messages++;
    updateContactPreview(currentContact.id, text);
    updateProfile(currentContact);
  }
}

// Generate AI response
function generateAIResponse(userMessage: string): string {
  const message = userMessage.toLowerCase();

  if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
    return getRandomResponse('greetings');
  } else if (message.includes('tired') || message.includes('sad') || message.includes('unhappy')) {
    return getRandomResponse('emotional');
  } else if (message.includes('?')) {
    return getRandomResponse('casual');
  } else {
    const responses = [...aiResponses.casual, ...aiResponses.compliments, ...aiResponses.questions];
    return responses[Math.floor(Math.random() * responses.length)];
  }
}

// Get random response from category
function getRandomResponse(category: keyof typeof aiResponses): string {
  const responses = aiResponses[category];
  return responses[Math.floor(Math.random() * responses.length)];
}

// Get current time string
function getCurrentTime(): string {
  return new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

// Setup event listeners
function setupEventListeners() {
  // Send button
  const sendBtn = document.getElementById('sendBtn');
  const messageInput = document.getElementById('messageInput') as HTMLInputElement;

  if (sendBtn && messageInput) {
    sendBtn.addEventListener('click', () => {
      sendMessage(messageInput.value);
    });

    messageInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        sendMessage(messageInput.value);
      }
    });
  }

  // Emoji button - insert common emojis
  const emojiButtons = document.querySelectorAll('.chat-input-area .icon-btn');
  if (emojiButtons[0]) {
    emojiButtons[0].addEventListener('click', () => {
      const emojis = ['😊', '❤️', '👍', '😂', '🎉', '💕', '✨', '🌟'];
      const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];
      if (messageInput) {
        messageInput.value += randomEmoji;
        messageInput.focus();
      }
    });
  }

  // Image button - show message
  if (emojiButtons[1]) {
    emojiButtons[1].addEventListener('click', () => {
      if (currentContact) {
        receiveMessage('📸 Photo sharing feature coming soon!');
      }
    });
  }

  // Media items click handlers
  setupMediaHandlers();

  // Search functionality
  const searchInput = document.querySelector('.search-bar input') as HTMLInputElement;
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const searchTerm = (e.target as HTMLInputElement).value.toLowerCase();
      filterContacts(searchTerm);
    });
  }

  // Voice call button
  const voiceCallBtn = document.getElementById('voiceCallBtn');
  const voiceModal = document.getElementById('voiceModal');
  const endVoiceCallBtn = document.getElementById('endVoiceCallBtn');

  if (voiceCallBtn && voiceModal && endVoiceCallBtn) {
    voiceCallBtn.addEventListener('click', () => {
      if (!currentContact) return;
      startVoiceCall();
    });

    endVoiceCallBtn.addEventListener('click', () => {
      endVoiceCall();
    });
  }

  // Video call button
  const videoCallBtn = document.getElementById('videoCallBtn');
  const videoModal = document.getElementById('videoModal');
  const endCallBtn = document.getElementById('endCallBtn');

  if (videoCallBtn && videoModal && endCallBtn) {
    videoCallBtn.addEventListener('click', () => {
      if (!currentContact) return;
      startVideoCall();
    });

    endCallBtn.addEventListener('click', () => {
      endVideoCall();
    });
  }

  // Mute buttons
  const muteBtn = document.getElementById('muteBtn');
  const muteVoiceBtn = document.getElementById('muteVoiceBtn');
  const cameraBtn = document.getElementById('cameraBtn');
  const speakerBtn = document.getElementById('speakerBtn');

  if (muteBtn) {
    muteBtn.addEventListener('click', () => {
      muteBtn.classList.toggle('muted');
    });
  }

  if (cameraBtn) {
    cameraBtn.addEventListener('click', () => {
      cameraBtn.classList.toggle('muted');
    });
  }

  if (muteVoiceBtn) {
    muteVoiceBtn.addEventListener('click', () => {
      muteVoiceBtn.classList.toggle('muted');
    });
  }

  if (speakerBtn) {
    speakerBtn.addEventListener('click', () => {
      speakerBtn.classList.toggle('muted');
    });
  }
}

// Filter contacts based on search term
function filterContacts(searchTerm: string) {
  const contactItems = document.querySelectorAll('.contact-item');
  let visibleCount = 0;

  contactItems.forEach((item) => {
    const contactId = parseInt(item.getAttribute('data-id') || '0');
    const contact = contacts.find(c => c.id === contactId);

    if (contact) {
      const nameMatch = contact.name.toLowerCase().includes(searchTerm);
      const previewMatch = contact.preview.toLowerCase().includes(searchTerm);

      if (nameMatch || previewMatch || searchTerm === '') {
        (item as HTMLElement).style.display = 'flex';
        visibleCount++;
      } else {
        (item as HTMLElement).style.display = 'none';
      }
    }
  });

  // Show "no results" message if needed
  const contactsList = document.getElementById('contactsList');
  let noResultsMsg = document.getElementById('noResultsMsg');

  if (visibleCount === 0 && searchTerm !== '') {
    if (!noResultsMsg) {
      noResultsMsg = document.createElement('div');
      noResultsMsg.id = 'noResultsMsg';
      noResultsMsg.style.cssText = 'padding: 48px 32px; text-align: center; color: var(--text-secondary);';
      noResultsMsg.innerHTML = `
        <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.3;">🔍</div>
        <div style="font-size: 15px; font-weight: 500;">No contacts found</div>
        <div style="font-size: 13px; margin-top: 8px; opacity: 0.7;">Try a different search term</div>
      `;
      contactsList?.appendChild(noResultsMsg);
    }
  } else if (noResultsMsg) {
    noResultsMsg.remove();
  }
}

// Setup media handlers
function setupMediaHandlers() {
  const mediaItems = document.querySelectorAll('.media-item');

  mediaItems.forEach((item, index) => {
    item.addEventListener('click', () => {
      if (!currentContact) return;

      const mediaTypes = ['Photos', 'Videos', 'Audio'];
      const mediaType = mediaTypes[index];
      const mediaEmojis = ['📸', '🎥', '🎵'];

      // Show media modal or send a message
      showMediaPreview(mediaType, mediaEmojis[index]);
    });
  });
}

// Show media preview
function showMediaPreview(type: string, emoji: string) {
  if (!currentContact) return;

  const messages = {
    'Photos': [
      `${emoji} Here are some photos I took recently!`,
      `${emoji} Just shared a beautiful sunset photo with you~`,
      `${emoji} Look at this amazing view!`
    ],
    'Videos': [
      `${emoji} I recorded a fun video for you!`,
      `${emoji} Check out this cool video I found!`,
      `${emoji} Sharing my latest vlog with you~`
    ],
    'Audio': [
      `${emoji} Listen to this song, it's amazing!`,
      `${emoji} I recorded a voice message for you~`,
      `${emoji} This music reminds me of you!`
    ]
  };

  const typeMessages = messages[type as keyof typeof messages] || [];
  const randomMessage = typeMessages[Math.floor(Math.random() * typeMessages.length)];

  receiveMessage(randomMessage);
}

// Voice call functions
let voiceCallTimer: number | null = null;
let voiceCallSeconds = 0;

function startVoiceCall() {
  if (!currentContact) return;

  const voiceModal = document.getElementById('voiceModal');
  const voiceAvatar = document.getElementById('voiceAvatar') as HTMLImageElement;
  const voiceCallName = document.getElementById('voiceCallName');
  const voiceStatus = document.getElementById('voiceStatus');
  const voiceTimer = document.getElementById('voiceTimer');

  if (voiceModal && voiceAvatar && voiceCallName && voiceStatus && voiceTimer) {
    voiceAvatar.src = currentContact.avatar;
    voiceCallName.textContent = currentContact.name;
    voiceStatus.textContent = 'Calling...';
    voiceTimer.textContent = '00:00';
    voiceModal.classList.add('active');

    // Update call count
    if (currentContact) {
      currentContact.calls++;
      updateProfile(currentContact);
    }

    // Simulate connection
    setTimeout(() => {
      voiceStatus.textContent = 'Connected';
      voiceCallSeconds = 0;
      voiceCallTimer = window.setInterval(() => {
        voiceCallSeconds++;
        const minutes = Math.floor(voiceCallSeconds / 60);
        const seconds = voiceCallSeconds % 60;
        voiceTimer.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }, 1000);
    }, 2000);
  }
}

function endVoiceCall() {
  const voiceModal = document.getElementById('voiceModal');
  if (voiceModal) {
    voiceModal.classList.remove('active');
  }
  if (voiceCallTimer) {
    clearInterval(voiceCallTimer);
    voiceCallTimer = null;
  }
  voiceCallSeconds = 0;
}

// Video call functions
let videoCallTimer: number | null = null;
let videoCallSeconds = 0;

function startVideoCall() {
  if (!currentContact) return;

  const videoModal = document.getElementById('videoModal');
  const videoFeed = document.getElementById('videoFeed') as HTMLImageElement;
  const videoCallName = document.getElementById('videoCallName');
  const callTimer = document.getElementById('callTimer');

  if (videoModal && videoFeed && videoCallName && callTimer) {
    videoFeed.src = currentContact.avatar;
    videoCallName.textContent = `Video call with ${currentContact.name}`;
    callTimer.textContent = '00:00';
    videoModal.classList.add('active');

    // Update call count
    if (currentContact) {
      currentContact.calls++;
      updateProfile(currentContact);
    }

    // Simulate connection
    setTimeout(() => {
      const overlay = document.querySelector('.video-overlay') as HTMLElement;
      if (overlay) overlay.style.display = 'none';

      videoCallSeconds = 0;
      videoCallTimer = window.setInterval(() => {
        videoCallSeconds++;
        const minutes = Math.floor(videoCallSeconds / 60);
        const seconds = videoCallSeconds % 60;
        callTimer.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }, 1000);
    }, 2000);
  }
}

function endVideoCall() {
  const videoModal = document.getElementById('videoModal');
  const overlay = document.querySelector('.video-overlay') as HTMLElement;

  if (videoModal) {
    videoModal.classList.remove('active');
  }
  if (overlay) {
    overlay.style.display = 'flex';
  }
  if (videoCallTimer) {
    clearInterval(videoCallTimer);
    videoCallTimer = null;
  }
  videoCallSeconds = 0;
}

// Initialize on load
initApp();

// Vite HMR
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    initApp();
  });
}
